'use client';

import { Button } from '@/components/ui/button';
import { useLanguage } from '@/lib/i18n/language-provider';
import { useRouter } from 'next/navigation';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Crown, AlertTriangle, Sparkles } from 'lucide-react';
import { navigateOrOpenBrowser } from '@/lib/tauri-navigation';

interface DemoLimitReachedProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  limitType: 'element' | 'connection' | 'cable' | 'processor' | 'cabinet';
  currentCount: number;
  maxCount: number;
}

export function DemoLimitReached({
  open,
  onOpenChange,
  limitType,
  currentCount,
  maxCount,
}: DemoLimitReachedProps) {
  const { t } = useLanguage();
  const router = useRouter();

  const getLimitInfo = () => {
    switch (limitType) {
      case 'element':
        return {
          title: t('demo.limit.element.title') || "Limite d'éléments atteinte",
          description: t('demo.limit.element.desc') || `En mode démo, vous pouvez ajouter jusqu'à ${maxCount} éléments. Passez en Pro pour un nombre illimité !`,
          icon: <Sparkles className="h-6 w-6" />,
        };
      case 'connection':
        return {
          title: t('demo.limit.connection.title') || 'Limite de connexions atteinte',
          description: t('demo.limit.connection.desc') || `En mode démo, vous pouvez créer jusqu'à ${maxCount} connexions. Passez en Pro pour connecter tous vos équipements !`,
          icon: <Sparkles className="h-6 w-6" />,
        };
      case 'cable':
        return {
          title: t('demo.limit.cable.title') || 'Limite de câbles atteinte',
          description: t('demo.limit.cable.desc') || `En mode démo, vous pouvez tracer jusqu'à ${maxCount} câbles. Passez en Pro pour un câblage complet !`,
          icon: <Sparkles className="h-6 w-6" />,
        };
      case 'processor':
        return {
          title: t('demo.limit.processor.title') || 'Limite de processeurs atteinte',
          description: t('demo.limit.processor.desc') || `En mode démo, vous pouvez ajouter jusqu'à ${maxCount} processeurs. Passez en Pro pour un nombre illimité !`,
          icon: <Sparkles className="h-6 w-6" />,
        };
      case 'cabinet':
        return {
          title: t('demo.limit.cabinet.title') || "Limite d'armoires atteinte",
          description: t('demo.limit.cabinet.desc') || `En mode démo, vous pouvez ajouter jusqu'à ${maxCount} armoire. Passez en Pro pour un nombre illimité !`,
          icon: <Sparkles className="h-6 w-6" />,
        };
    }
  };

  const info = getLimitInfo();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-orange-500/10 text-orange-500">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <DialogTitle className="text-xl">{info.title}</DialogTitle>
          </div>
          <DialogDescription className="text-base">
            {info.description}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="flex items-center justify-center gap-2 p-4 rounded-lg bg-muted/50 border border-border">
            <span className="text-4xl font-bold text-orange-500">{currentCount}</span>
            <span className="text-2xl text-muted-foreground">/</span>
            <span className="text-4xl font-bold">{maxCount}</span>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button
            size="lg"
            className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
            onClick={() => {
              onOpenChange(false);
              navigateOrOpenBrowser('/pro', router);
            }}
          >
            <Crown className="mr-2 h-5 w-5" />
            {t('demo.limit.upgradeButton') || 'Passer en Pro'}
          </Button>
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => onOpenChange(false)}
          >
            {t('demo.limit.continueButton') || 'Continuer en mode démo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
